import React, { useMemo } from "react";
import { ArrowLeft, Bell } from "react-feather";

import type { ProfileShortcut } from "../../../../types/profileShortcuts";

import ProfileActionCard from "./ProfileActionCard";

import styles from "./FeatureQuickAccessPreview.module.css";

export interface FeatureQuickAccessPreviewProps {
  shortcuts: ProfileShortcut[];
}

/**
 * Hardcoded mock copy for the illustrative parts of this preview (header,
 * avatar, balance, banner, empty state). Deliberately plain string literals
 * rather than `AppStrings`: this isn't real product copy an admin or a TCM
 * translator would ever touch — it's fake demo data ("PlayerOne23",
 * "$128.50") standing in for whatever a real user's profile would show, so
 * it doesn't need AGENTS.md §14's AppStrings/TCM treatment. `AppStrings` is
 * still used for the real UI chrome around this component — e.g. the
 * "Preview" card heading in `FeatureQuickAccess.tsx`. Exported only so the
 * test file can assert against these same literals instead of duplicating
 * them.
 */
export const PREVIEW_DEMO_COPY = {
  backAria: "Back",
  notificationsAria: "Notifications",
  avatarAlt: "Profile avatar",
  title: "Profile",
  username: "PlayerOne23",
  memberSince: "Member since 2023",
  cashLabel: "Cash",
  cashValue: "$128.50",
  coinsLabel: "Coins",
  coinsValue: "42,300",
  bannerTitle: "Weekend Reload Bonus",
  bannerSubtitle: "Claim before it expires",
  noLiveShortcuts: "No shortcuts are live",
} as const;

/**
 * Feature Quick Access → live preview (right-hand mobile mock).
 *
 * A representative, in-app mock of the `trivelta-webapp` `/profile` route
 * (see `src/app/profile/page.tsx`: header → `ProfileContainer` →
 * `BalanceCardSection` → Braze banner → `ProfileQuickActions`). Per
 * FE-2349's scope, only the piece Tab Configuration actually owns — the
 * profile "quick actions" shortcuts row — is driven live from `shortcuts`;
 * everything above it (header, avatar, balance, promo banner) is
 * `PREVIEW_DEMO_COPY`, a static illustrative mock matching that page's
 * layout but not wired to any real data source.
 *
 * The shortcuts row mirrors `ProfileQuickActions`' own filtering: only
 * `isLive` shortcuts render, in `order`, each with a `ProfileActionCard`
 * that shows a hardcoded illustrative badge count (not real unread data).
 * Reordering, toggling live/off, or editing a shortcut's name/icon in the
 * Tab Configuration table on the left updates this row immediately, since
 * both read the same `shortcuts` state from the parent page.
 */
const FeatureQuickAccessPreview: React.FC<FeatureQuickAccessPreviewProps> = ({
  shortcuts,
}) => {
  const visibleShortcuts = useMemo(
    () =>
      shortcuts
        .filter((shortcut) => shortcut.isLive)
        .slice()
        .sort((a, b) => a.order - b.order),
    [shortcuts],
  );

  return (
    <aside className={styles.card} data-testid="feature-quick-access-preview-phone">
      <div className={styles.phone}>
        <div className={styles.screen}>
          <header className={styles.appHeader}>
            <span className={styles.iconButton} aria-label={PREVIEW_DEMO_COPY.backAria}>
              <ArrowLeft size={20} />
            </span>
            <h2 className={styles.appTitle}>{PREVIEW_DEMO_COPY.title}</h2>
            <span
              className={styles.iconButton}
              aria-label={PREVIEW_DEMO_COPY.notificationsAria}
            >
              <Bell size={20} />
            </span>
          </header>

          <section className={styles.profileSection}>
            <span
              className={styles.avatar}
              role="img"
              aria-label={PREVIEW_DEMO_COPY.avatarAlt}
            >
              {PREVIEW_DEMO_COPY.username.slice(0, 2).toUpperCase()}
            </span>
            <span className={styles.username}>{PREVIEW_DEMO_COPY.username}</span>
            <span className={styles.memberSince}>
              {PREVIEW_DEMO_COPY.memberSince}
            </span>
          </section>

          <section className={styles.balanceRow}>
            <div className={styles.balancePill}>
              <span className={styles.balanceLabel}>
                {PREVIEW_DEMO_COPY.cashLabel}
              </span>
              <span className={styles.balanceValue}>
                {PREVIEW_DEMO_COPY.cashValue}
              </span>
            </div>
            <div className={styles.balancePill}>
              <span className={styles.balanceLabel}>
                {PREVIEW_DEMO_COPY.coinsLabel}
              </span>
              <span className={styles.balanceValue}>
                {PREVIEW_DEMO_COPY.coinsValue}
              </span>
            </div>
          </section>

          <section className={styles.bannerCard}>
            <span className={styles.bannerTitle}>
              {PREVIEW_DEMO_COPY.bannerTitle}
            </span>
            <span className={styles.bannerSubtitle}>
              {PREVIEW_DEMO_COPY.bannerSubtitle}
            </span>
          </section>

          <section
            className={styles.quickActions}
            data-testid="preview-quick-actions"
          >
            {visibleShortcuts.length === 0 ? (
              <p className={styles.emptyState}>
                {PREVIEW_DEMO_COPY.noLiveShortcuts}
              </p>
            ) : (
              visibleShortcuts.map((shortcut) => (
                <ProfileActionCard key={shortcut.id} shortcut={shortcut} />
              ))
            )}
          </section>
        </div>
      </div>
    </aside>
  );
};

export default FeatureQuickAccessPreview;
